import { useEffect, useState } from 'react';
import { invokeAppShellReadModel } from '@/lib/readModels';
import { useDeferredActivation } from '@/hooks/useDeferredActivation';

interface UseAppShellSummaryOptions {
  organizationId?: string;
  agentOrganizationId?: string;
  userId?: string;
  deferUntilIdle?: boolean;
}

interface AppShellSummaryState {
  activeAlertCount: number;
  unreadNotificationCount: number;
  currentUserProfile: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
  isLoading: boolean;
}

const APP_SHELL_SUMMARY_TTL_MS = 30_000;
const appShellSummaryCache = new Map<string, { data: AppShellSummaryState; expiresAt: number }>();
const appShellSummaryInflight = new Map<string, Promise<AppShellSummaryState>>();

function buildCacheKey(options: UseAppShellSummaryOptions) {
  return `${options.organizationId ?? 'none'}:${options.agentOrganizationId ?? 'none'}:${options.userId ?? 'anonymous'}`;
}

function getDefaultState(): AppShellSummaryState {
  return {
    activeAlertCount: 0,
    unreadNotificationCount: 0,
    currentUserProfile: {
      first_name: null,
      last_name: null,
      avatar_url: null,
    },
    isLoading: true,
  };
}

async function fetchAppShellSummary(options: Required<Pick<UseAppShellSummaryOptions, 'organizationId'>> & {
  agentOrganizationId?: string;
  userId?: string;
}) {
  const cacheKey = buildCacheKey(options);
  const cached = appShellSummaryCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  let inflight = appShellSummaryInflight.get(cacheKey);
  if (!inflight) {
    inflight = invokeAppShellReadModel({
      organizationId: options.organizationId,
      agentOrganizationId: options.agentOrganizationId,
    }, {
      expectedUserId: options.userId,
    }).then((response) => {
      const data = {
        activeAlertCount: response.activeAlertCount,
        unreadNotificationCount: response.unreadNotificationCount,
        currentUserProfile: response.currentUserProfile,
        isLoading: false,
      };
      appShellSummaryCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + APP_SHELL_SUMMARY_TTL_MS,
      });
      return data;
    }).finally(() => {
      appShellSummaryInflight.delete(cacheKey);
    });
    appShellSummaryInflight.set(cacheKey, inflight);
  }

  return inflight;
}

export function resetAppShellSummaryCacheForTests() {
  appShellSummaryCache.clear();
  appShellSummaryInflight.clear();
}

export function useAppShellSummary(options: UseAppShellSummaryOptions): AppShellSummaryState {
  const shouldActivate = useDeferredActivation(
    Boolean(options.organizationId) && options.deferUntilIdle === true,
  );
  const [state, setState] = useState<AppShellSummaryState>(() => {
    if (!options.organizationId) {
      return { ...getDefaultState(), isLoading: false };
    }

    const cached = appShellSummaryCache.get(buildCacheKey(options));
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    return getDefaultState();
  });

  useEffect(() => {
    if (!options.organizationId) {
      setState({
        activeAlertCount: 0,
        unreadNotificationCount: 0,
        currentUserProfile: {
          first_name: null,
          last_name: null,
          avatar_url: null,
        },
        isLoading: false,
      });
      return;
    }

    if (!options.userId) {
      setState((current) => ({
        ...current,
        isLoading: false,
      }));
      return;
    }

    if (options.deferUntilIdle === true && !shouldActivate) {
      setState((current) => ({
        ...current,
        isLoading: false,
      }));
      return;
    }

    let isCancelled = false;

    void fetchAppShellSummary({
      organizationId: options.organizationId,
      agentOrganizationId: options.agentOrganizationId,
      userId: options.userId,
    }).then((data) => {
      if (!isCancelled) {
        setState(data);
      }
    }).catch(() => {
      if (!isCancelled) {
        setState({
          activeAlertCount: 0,
          unreadNotificationCount: 0,
          currentUserProfile: {
            first_name: null,
            last_name: null,
            avatar_url: null,
          },
          isLoading: false,
        });
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [options.agentOrganizationId, options.deferUntilIdle, options.organizationId, options.userId, shouldActivate]);

  return state;
}
